import React, {useState, useCallback, useEffect} from "react";
import { useNavigate } from "react-router-dom";
import { userService } from "../../auth/user-service";
import { userAPI } from "../../api";
import WplButton from "../../components/wpl-button/WplButton";
import WplInput from "../../components/wpl-input/WplInput";
import WplPasswordInput from "../../components/wpl-password-input/WplPasswordInput";
import TitledModal from "../../components/titled-modal/TitledModal";
import { useKeyboardShortcut } from "../../components/UseKeyboardShortcut";
import TurbineAnimation from "../../components/turbine-animation/TurbineAnimation";
import { useBranding } from "../../branding/BrandingProvider";
import "./login.css";

export default function Login() {
  const brand = useBranding();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [resetEmail, setResetEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [showResetModal, setShowResetModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [emailError, setEmailError] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [resetEmailError, setResetEmailError] = useState("");

  const navigate = useNavigate();

  const isValidEmail = (email) => {
    const regex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    return regex.test(email);
  };

  const doLogin = useCallback(() => {
    // Clear previous errors
    setEmailError("");
    setPasswordError("");

    // Validate email format
    if (!email || !isValidEmail(email)) {
      setEmailError("Please enter a valid email address");
      return;
    }

    if (!password) {
      setPasswordError("Password is required");
      return;
    }

    setLoading(true);
    userService
      .login(email, password)
      .then(() => {
        setLoading(false);
        const redir_path = localStorage.getItem("redirAfterLogin");
        navigate(redir_path && redir_path !== "/login" ? redir_path : "/");
      })
      .catch(() => {
        setLoading(false);
        setEmailError("Wrong email or password");
        setPasswordError("Wrong email or password");
      });
  }, [email, password, navigate]);

    // dashboard functionality - automatically submit the form if the flag is there
    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        if (params.get("dashboard-auto-submit-login") !== null) {
            setTimeout(() => {
                localStorage.setItem("redirAfterLogin", "/internal/wpl-dashboard");
                doLogin();
            }, 3000);
        }
    }, [doLogin]);

  const createPasswordReset = useCallback(() => {
    // Clear previous errors
    setResetEmailError("");

    // Validate email format
    if (!resetEmail || !isValidEmail(resetEmail)) {
      setResetEmailError("Please enter a valid email address");
      return;
    }

    setLoading(true);
    userAPI
      .requestPasswordReset(resetEmail)
      .then(() => {
        setLoading(false);
        setShowResetModal(false);
        setResetEmail("");
        setShowSuccessModal(true);
      })
      .catch(() => {
        setLoading(false);
        setResetEmailError("No account found with that email");
      });
  }, [resetEmail]);

  const handleOpenResetModal = useCallback(() => {
    setShowResetModal(true);
    setResetEmailError("");
  }, []);

  useKeyboardShortcut(["Enter"], doLogin);

  return (
    <main className="login-page">
      <div className="bg-img" />
      <div className={`overlay ${loading ? "loading" : ""}`}>
        <TurbineAnimation overlayClassname="darker-overlay" />
      </div>
      <div className="login-wrapper">
        {brand.homepageUrl ? (
          <a href={brand.homepageUrl} target="_blank" rel="noopener noreferrer">
            {brand.logo && <img alt={brand.name} src={brand.logo} className="login-logo" />}
          </a>
        ) : (
          brand.logo && <img alt={brand.name} src={brand.logo} className="login-logo login-logo-full" />
        )}

        <form autoComplete="on" className="login-form">
          <WplInput
            title="Email"
            type="email"
            value={email}
            onChanged={setEmail}
            error={emailError}
            placeholder="Enter your email"
            autoComplete="username"
          />
          <WplPasswordInput
            title="Password"
            value={password}
            onChanged={setPassword}
            error={passwordError}
            placeholder="Enter your password"
            autoComplete="current-password"
          />
          <WplButton
            value="Log in"
            loading={loading}
            onClick={doLogin}
            big
          />
        </form>

        <div className="forgot-pw-container">
          <button onClick={handleOpenResetModal} className="forgot-pw-btn">
            Forgot your password?
          </button>
        </div>
      </div>

      {showResetModal && (
        <TitledModal
          title="Reset Password"
          onClose={() => {
            setShowResetModal(false);
            setResetEmail("");
            setResetEmailError("");
          }}
        >
          <div className="reset-password-modal-content">
            <p>Enter your email to receive a password reset link</p>
            <WplInput
              title="Email"
              type="email"
              value={resetEmail}
              onChanged={setResetEmail}
              error={resetEmailError}
              placeholder="Enter your email"
              autoComplete="username"
            />
            <WplButton
              value="Reset Password"
              loading={loading}
              onClick={createPasswordReset}
              big
            />
          </div>
        </TitledModal>
      )}

      {showSuccessModal && (
        <TitledModal
          title="Email Sent!"
          onClose={() => setShowSuccessModal(false)}
        >
          <div className="success-modal-content">
            <p>Please check your inbox for further instructions.</p>
            <WplButton
              value="OK"
              onClick={() => setShowSuccessModal(false)}
              big
            />
          </div>
        </TitledModal>
      )}
    </main>
  );
}
